import { useMemo } from 'react';
import { Segment } from 'sl-api-connector';
import { useDorisMetricByWeekId } from 'src/serverProxy/useDorisMetricByWeekId';
import {
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { AtlasSegmentDataAdapter } from '../adapters/AtlasSegmentDataAdapter';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';

const convertSegmentConditionsToDorisParams = (segment: any, isPending: boolean) => {
  const params: {
    categoryIds?: number[];
    subcategoryIds?: number[];
    brandIds?: number[];
    segmentIds?: string[][];
    minRetailPrice?: number;
    maxRetailPrice?: number;
    keywords?: string[];
  } = {};

  // When segment is completed (not pending), use the segment ID
  // When pending, we can't query by segment ID as it's still processing
  if (segment?.id && !isPending) {
    params.segmentIds = [[segment.id]];
    // Return early if we have a segment ID - no need for conditions
    return params;
  }

  // Fall back to conditions for pending segments or segments without IDs
  if (segment?.conditions?.termFilters) {
    segment.conditions.termFilters.forEach((termFilter: any) => {
      if (!termFilter.values || termFilter.values.length === 0) return;

      switch (termFilter.fieldName) {
        case 'categoryId':
          params.categoryIds = termFilter.values;
          break;
        case 'subCategoryId':
          params.subcategoryIds = termFilter.values;
          break;
        case 'brandId':
          params.brandIds = termFilter.values;
          break;
        case 'searchKeywordFuzzy':
        case 'searchTermFuzzy':
          params.keywords = termFilter.values;
          break;
        default:
          break;
      }
    });
  }

  if (segment?.conditions?.rangeFilters) {
    segment.conditions.rangeFilters.forEach((rangeFilter: any) => {
      switch (rangeFilter.fieldName) {
        case 'retailPrice':
          if (rangeFilter.minValue !== undefined) params.minRetailPrice = rangeFilter.minValue;
          if (rangeFilter.maxValue !== undefined) params.maxRetailPrice = rangeFilter.maxValue;
          break;
        default:
          break;
      }
    });
  }

  return params;
};

interface UseAtlasSegmentDataProps {
  segment: Segment;
  fieldName?: string;
  isPending?: boolean;
}

export const useAtlasSegmentData = ({
  segment,
  fieldName = 'retailSales',
  isPending = false
}: UseAtlasSegmentDataProps) => {
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const formatter = useMetricFormatter();

  const metricTypeMapping = {
    retailSales: DorisMetricTypeEnum.RetailSales,
    unitsSold: DorisMetricTypeEnum.UnitsSold,
    retailPrice: DorisMetricTypeEnum.RetailPrice
  };

  const dorisMetricType =
    metricTypeMapping[fieldName as keyof typeof metricTypeMapping] || DorisMetricTypeEnum.RetailSales;

  const dorisParams = useMemo(() => convertSegmentConditionsToDorisParams(segment, isPending), [segment, isPending]);

  // Use a stable query key when pending to prevent fetching
  const queryKey = isPending ? `segment-pending-${fieldName}` : `segment-${segment?.id}-${fieldName}-${isPending}`;

  // Include isPending in the query key to trigger a refetch when segment processing completes
  // This ensures the card updates with actual data once the segment transitions from pending to completed
  const rawData = useDorisMetricByWeekId({
    queryKey,
    methodName: DorisMetricEndPointEnum.SalesEndPoint,
    metricType: dorisMetricType,
    resultCount: 1200,
    baseParamsBuilder: () => (isPending ? {} : dorisParams) // Return empty params when pending
  });

  const adapter = useMemo(() => {
    return new AtlasSegmentDataAdapter(rawData, fieldName, formatter, comparisonTimePeriod);
  }, [rawData, fieldName, formatter, comparisonTimePeriod]);

  // If segment is pending, we show loading state
  // Once it completes (isPending = false), the query will run with segment ID
  return {
    adapter,
    isLoading: isPending || adapter.isLoading(),
    hasValidData: !isPending && adapter.hasValidData()
  };
};
